import type { dia } from '@joint/core'

/**
 * 텍스트 라벨 객체의 박스/폰트/정렬 규칙.
 *
 * 모델: 라벨의 진짜 소스는 (text, fontSize) 두 attr 이다. 선택/hit 박스(body)는
 * 항상 글자에 맞춰 파생된다 — 너비=글자 잉크 폭(+여백), 높이=글자 라인박스.
 * 따라서 "박스 == 보이는 글자 == 선택 영역" 이 항상 성립한다.
 *
 * - 생성/이름변경/로드: hugLabelBoxToText 로 (text,fontSize) → box 재계산.
 * - 리사이즈(핸들 드래그): 드래그한 박스 크기에 맞는 fontSize 를 역산해서
 *   폰트를 키우거나 줄인다(라벨창에 맞춰 폰트 자동 스케일). fontSize 만 바뀌고
 *   box 는 다시 글자에 맞춰 파생되므로 저장/재로드에도 그대로 유지된다.
 *
 * 가운데 정렬은 standard.Rectangle 기본 attr(x/y calc, textAnchor middle)에
 * 맡기고, 과거 라벨이 갖고 있던 refX/refY 는 제거한다(기본 x/y 와 겹쳐 정렬을
 * 망가뜨리고, 기본값이라 toJSON 누락→재로드 시 재발했음).
 *
 * 모든 값은 paper 좌표(= fontSize 와 동일 단위)라 줌/normalize 와 무관하다.
 */

/** 라벨 텍스트 기본 폰트 — useObjectCreation 의 label attr 과 동일하게 유지 */
export const LABEL_FONT_FAMILY = 'system-ui, "Segoe UI", Roboto, sans-serif'

/**
 * 동/구역 이름 라벨 텍스트 색 — 빨강 고정.
 *
 * 캔버스 배경은 테마(Light/Dark)에 따라 useThemeSync 가 바꾸지만 라벨 글자색은
 * 셀 생성 시 고정된다. 기존 어두운 회색(#1f2937/#111827)은 Dark 배경에서 묻혀
 * 안 보였다(사용자 보고). 빨강은 밝은/어두운 배경 양쪽에서 충분히 대비된다.
 */
export const LABEL_TEXT_COLOR = '#EF4444'

const DEFAULT_FONT_SIZE = 12
/** 글자 잉크가 좌우 테두리에 닿지 않도록 하는 여백 (fontSize 비율, 한 쪽) */
const PADDING_X_RATIO = 0.08
const MIN_FONT_SIZE = 4
const MAX_FONT_SIZE = 4000
/** 선형 스케일 기준 폰트 크기 (glyph 폭/라인높이는 fontSize 에 정비례) */
const REF_FONT = 100

export interface LabelBox {
  /** 박스 너비 (paper 단위) = 글자 잉크 폭 + 좌우 여백 */
  width: number
  /** 박스 높이 (paper 단위) = 글자 라인박스 높이 */
  height: number
}

let sharedCtx: CanvasRenderingContext2D | null | undefined

function getContext(): CanvasRenderingContext2D | null {
  if (sharedCtx !== undefined) return sharedCtx
  sharedCtx =
    typeof document !== 'undefined'
      ? document.createElement('canvas').getContext('2d')
      : null
  return sharedCtx
}

/** 기준 폰트(REF_FONT)에서의 글자 잉크 폭 + 라인박스 높이. 측정 불가면 null. */
function measureRef(
  safeText: string,
  fontFamily: string,
): { inkW: number; lineH: number } | null {
  const ctx = getContext()
  if (!ctx) return null
  ctx.font = `${REF_FONT}px ${fontFamily}`
  const m = ctx.measureText(safeText)
  const lineH = m.fontBoundingBoxAscent + m.fontBoundingBoxDescent
  if (m.width > 0 && Number.isFinite(lineH) && lineH > 0) {
    return { inkW: m.width, lineH }
  }
  return null
}

/**
 * (text, fontSize) → 글자에 딱 맞는 박스(잉크 폭 × 라인박스 높이).
 */
export function measureLabelBox(
  text: string,
  fontSize: number,
  fontFamily: string = LABEL_FONT_FAMILY,
): LabelBox {
  const safeText = text.trim() || ' '
  const padX = fontSize * PADDING_X_RATIO
  const ref = measureRef(safeText, fontFamily)
  if (ref) {
    return {
      width: (ref.inkW * fontSize) / REF_FONT + padX * 2,
      height: (ref.lineH * fontSize) / REF_FONT,
    }
  }
  // 측정 불가 환경(SSR/jsdom) — fontSize·글자수 기반 근사 폴백
  return {
    width: safeText.length * fontSize * 0.6 + padX * 2,
    height: fontSize * 1.2,
  }
}

/**
 * 드래그한 박스(boxW × boxH)에 글자가 가장 크게 들어가는 fontSize 를 역산한다.
 * measureLabelBox 의 정확한 역함수라 hug→fit→hug 가 idempotent 하다.
 */
export function computeFitFontSize(
  text: string,
  boxW: number,
  boxH: number,
  fontFamily: string = LABEL_FONT_FAMILY,
): number {
  const safeText = text.trim() || ' '
  const ref = measureRef(safeText, fontFamily)
  let fontByW: number
  let fontByH: number
  if (ref) {
    // boxW = inkW*f/REF + 2*padX*f = f*(inkW/REF + 2*PADDING_X_RATIO)
    fontByW = boxW / (ref.inkW / REF_FONT + 2 * PADDING_X_RATIO)
    fontByH = (boxH * REF_FONT) / ref.lineH
  } else {
    fontByW = boxW / (safeText.length * 0.6 + 2 * PADDING_X_RATIO)
    fontByH = boxH / 1.2
  }
  const fit = Math.min(fontByW, fontByH)
  return Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, fit))
}

/** 가운데 정렬을 standard.Rectangle 기본 attr 로 맞추고 stale refX/refY 제거 */
function setLabelCentering(element: dia.Element): void {
  element.removeAttr('label/refX')
  element.removeAttr('label/refY')
  element.attr('label/x', 'calc(w/2)')
  element.attr('label/y', 'calc(h/2)')
  element.attr('label/textAnchor', 'middle')
  element.attr('label/textVerticalAnchor', 'middle')
}

/**
 * 현재 (text, fontSize) 기준으로 box 를 글자에 맞춰 다시 계산한다(중심 유지).
 * 생성/이름변경/로드/리사이즈 종료에서 공통 사용.
 */
export function hugLabelBoxToText(element: dia.Element): void {
  const fontSize = Number(element.attr('label/fontSize')) || DEFAULT_FONT_SIZE
  const fontFamily =
    (element.attr('label/fontFamily') as string) || LABEL_FONT_FAMILY
  const text = (element.attr('label/text') as string) ?? ''
  const { width, height } = measureLabelBox(text, fontSize, fontFamily)
  const center = element.getBBox().center()
  element.resize(width, height)
  element.position(center.x - width / 2, center.y - height / 2)
  setLabelCentering(element)
}

/**
 * 그래프 로드 직후 호출. 저장된 모든 라벨(kind==='label')을 현재 (text,fontSize)
 * 기준으로 재정규화해, 구버전 코드로 만들어진 라벨도 박스=글자=선택영역 +
 * 가운데 정렬이 되도록 보정한다.
 */
export function normalizeLabelElements(graph: dia.Graph): void {
  for (const cell of graph.getCells()) {
    const data = cell.get('data')
    if (
      data &&
      data.kind === 'label' &&
      typeof (cell as dia.Element).resize === 'function'
    ) {
      hugLabelBoxToText(cell as dia.Element)
    }
  }
}
